/**
 * La fiche d'un retour fournisseur (V1.6-10A).
 *
 * Ce que cet écran tient :
 *
 * * **préparer n'est pas envoyer.** Un brouillon se corrige tant que le colis
 *   n'est pas parti ; une fois finalisé, le stock est sorti et seule une
 *   transaction compensatoire le corrigerait ;
 * * **un retour n'annule pas une réception.** La réception d'origine reste
 *   telle quelle : cent sont arrivées, dix repartent. Les deux faits sont vrais,
 *   et effacer le premier rendrait l'histoire fausse ;
 * * **rien n'est recalculé ici.** Ce qui est renvoyable, les droits
 *   (`can_edit`, `can_finalize`) et le refus de sortir plus que ce qu'on
 *   possède viennent tous du serveur ;
 * * **aucun montant.** Un retour V1.6-10A est physique. Le numéro
 *   d'autorisation du fournisseur sert à retrouver le colis, pas à valoir de
 *   l'argent.
 */
import { Link, useParams } from "react-router-dom";
import { PackageX, Undo2 } from "lucide-react";

import { useFinalizeSupplierReturn, useSupplierReturn } from "@/api/supplier-returns";
import type { SupplierReturnDetail } from "@/api/types";
import { Button } from "@/components/Button";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { TableShell, Td, Th, Tr } from "@/components/DataTable";
import { ErrorState, TableSkeleton } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/components/toast-context";
import { Page } from "@/layout/Page";
import { PageHeader } from "@/layout/PageHeader";
import {
  formatDateTime,
  formatQuantityWithUnit,
  formatSupplierReturnReason,
  formatSupplierReturnStatus,
} from "@/lib/format";
import { reportMutationError } from "@/lib/mutation-errors";
import { supplierReturnTone } from "@/lib/stock";

export function SupplierReturnPage() {
  const { returnId } = useParams<{ returnId: string }>();
  const { data, isLoading, error, refetch } = useSupplierReturn(returnId);

  if (isLoading) {
    return (
      <Page>
        <TableSkeleton rows={5} columns={4} />
      </Page>
    );
  }
  if (error || !data) {
    return (
      <Page>
        <ErrorState error={error} onRetry={() => void refetch()} />
      </Page>
    );
  }

  const found = data.supplier_return;
  return (
    <Page>
      <PageHeader
        title={found.code}
        subtitle={found.supplier_name}
        actions={
          <StatusBadge tone={supplierReturnTone(found.status)}>
            {formatSupplierReturnStatus(found.status)}
          </StatusBadge>
        }
      />

      <div className="grid gap-4 @4xl:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="min-w-0 space-y-4">
          <ReturnLines detail={data} />
        </div>
        <div className="space-y-4">
          <Origin detail={data} />
          <Decisions detail={data} />
        </div>
      </div>
    </Page>
  );
}

function ReturnLines({ detail }: { detail: SupplierReturnDetail }) {
  const found = detail.supplier_return;
  const draft = found.status === "DRAFT";

  return (
    <Card>
      <CardHeader
        title="Articles renvoyés"
        subtitle={
          draft
            ? "Rien n'est encore sorti du stock. Finaliser écrira le mouvement."
            : "Le stock est sorti. Chaque ligne porte son mouvement au grand livre."
        }
      />
      <div className="hidden md:block">
        <TableShell>
          <thead>
            <tr>
              <Th className="max-w-0">Article</Th>
              <Th align="right" className="whitespace-nowrap">
                Quantité
              </Th>
              <Th className="whitespace-nowrap">Motif</Th>
              <Th className="hidden whitespace-nowrap @3xl:table-cell">Mouvement</Th>
            </tr>
          </thead>
          <tbody>
            {found.lines.map((line) => (
              <Tr key={line.id}>
                <Td className="max-w-0">
                  <span className="block truncate font-medium text-ink">
                    {line.description ?? "Article"}
                  </span>
                </Td>
                <Td align="right" className="tnum whitespace-nowrap text-[13px] text-ink">
                  {formatQuantityWithUnit(line.quantity, line.unit)}
                </Td>
                <Td className="text-[13px] text-ink-muted">
                  {formatSupplierReturnReason(line.reason_code)}
                  {line.reason_text && (
                    <span className="block text-[11px] text-ink-subtle">{line.reason_text}</span>
                  )}
                </Td>
                <Td className="hidden font-mono text-[11px] text-ink-subtle @3xl:table-cell">
                  {line.transaction_id ? line.transaction_id.slice(0, 8) : "—"}
                </Td>
              </Tr>
            ))}
          </tbody>
        </TableShell>
      </div>

      <ul className="divide-y divide-border/60 md:hidden">
        {found.lines.map((line) => (
          <li key={line.id} className="px-3 py-3">
            <p className="text-[14px] font-medium text-ink">{line.description ?? "Article"}</p>
            <p className="tnum mt-0.5 text-[13px] text-ink">
              {formatQuantityWithUnit(line.quantity, line.unit)}
            </p>
            <p className="mt-0.5 text-[12px] text-ink-muted">
              {formatSupplierReturnReason(line.reason_code)}
              {line.reason_text ? ` — ${line.reason_text}` : ""}
            </p>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function Origin({ detail }: { detail: SupplierReturnDetail }) {
  const found = detail.supplier_return;
  return (
    <Card>
      <CardHeader title="Provenance" />
      <CardBody>
        <dl className="space-y-2 text-[13px]">
          <div>
            <dt className="text-ink-subtle">Fournisseur</dt>
            <dd className="text-ink">{found.supplier_name}</dd>
          </div>
          {found.purchase_order_id && (
            <div>
              <dt className="text-ink-subtle">Commande</dt>
              <dd>
                <Link
                  to={`/commandes/${found.purchase_order_id}`}
                  className="font-mono text-[12px] text-brand hover:underline"
                >
                  Voir la commande
                </Link>
              </dd>
            </div>
          )}
          <div>
            <dt className="text-ink-subtle">Autorisation du fournisseur</dt>
            <dd className="font-mono text-[12px] text-ink">{found.rma_reference ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-ink-subtle">Préparé par</dt>
            <dd className="text-ink">
              {found.created_by} · {formatDateTime(found.created_at)}
            </dd>
          </div>
          {found.finalized_at && (
            <div>
              <dt className="text-ink-subtle">Envoyé par</dt>
              <dd className="text-ink">
                {found.finalized_by} · {formatDateTime(found.finalized_at)}
              </dd>
            </div>
          )}
          {found.notes && (
            <div>
              <dt className="text-ink-subtle">Notes</dt>
              <dd className="text-ink">{found.notes}</dd>
            </div>
          )}
        </dl>
      </CardBody>
    </Card>
  );
}

/**
 * Finaliser : **le seul chemin vers le grand livre.**
 *
 * Le bouton n'apparaît que si le serveur le dit. Ce n'est pas une politesse de
 * plus : le même refus est appliqué là où il compte, et l'écran ne fait que ne
 * pas proposer ce qui serait refusé.
 */
function Decisions({ detail }: { detail: SupplierReturnDetail }) {
  const toast = useToast();
  const finalize = useFinalizeSupplierReturn();
  const found = detail.supplier_return;

  if (found.status === "FINALIZED") {
    return (
      <Card>
        <CardHeader title="Décisions" />
        <CardBody>
          <p className="text-[13px] text-ink-muted">
            Le colis est parti et le stock est sorti. Un retour envoyé ne se corrige plus : une
            erreur se répare par un mouvement d'inventaire, pas en réécrivant celui-ci.
          </p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader title="Décisions" />
      <CardBody className="space-y-3">
        <p className="text-[13px] text-ink-muted">
          Rien n'est encore sorti. Finaliser fait sortir la marchandise du stock, tout ou rien,
          et scelle le retour.
        </p>
        <Button
          variant="primary"
          fullWidth
          disabled={!detail.can_finalize || finalize.isPending}
          loading={finalize.isPending}
          onClick={() =>
            finalize.mutate(
              { returnId: found.id },
              {
                onSuccess: () =>
                  toast.success(
                    "Retour envoyé",
                    "Le stock est sorti. Le mouvement est écrit au grand livre.",
                  ),
                onError: (error) => reportMutationError(toast, "Finalisation refusée", error),
              },
            )
          }
        >
          <PackageX className="size-4" aria-hidden />
          Finaliser le retour
        </Button>
        {!detail.can_finalize && (
          <p className="text-[12px] text-ink-subtle">
            <Undo2 className="mr-1 inline size-3" aria-hidden />
            Votre rôle ne permet pas de faire sortir du stock.
          </p>
        )}
      </CardBody>
    </Card>
  );
}
